import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Eye, FileText, Loader2, Plus, Radio, Send, Trash2, UserPlus, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Configurações > Monitoramento — seção Boletins (Fase 1001, D-08).
 *
 * Nome, frequência e destinatários são do usuário, não do código. A vaga
 * (diário/semanal/mensal) continua existindo porque o recorte dos DADOS é o que
 * o backend sabe montar; o rótulo e o calendário de envio são editáveis.
 *
 * Destinatários: incluir/excluir usuários do RBAC, ou um contato avulso só deste
 * boletim (sem cadastro). Checkbox sobre quatro nomes fixos não é gerenciamento.
 */
const DIAS_DA_SEMANA = [
  { valor: 0, rotulo: 'Segunda-feira' },
  { valor: 1, rotulo: 'Terça-feira' },
  { valor: 2, rotulo: 'Quarta-feira' },
  { valor: 3, rotulo: 'Quinta-feira' },
  { valor: 4, rotulo: 'Sexta-feira' },
  { valor: 5, rotulo: 'Sábado' },
  { valor: 6, rotulo: 'Domingo' },
];

const FREQUENCIAS = [
  { valor: 'daily', rotulo: 'Todo dia', detalhe: 'Retrato do dia anterior, hora a hora' },
  { valor: 'weekly', rotulo: 'Toda semana', detalhe: 'Retrato da semana anterior, dia a dia' },
  { valor: 'monthly', rotulo: 'Todo mês', detalhe: 'Retrato do mês anterior, dia a dia' },
];

const HORAS = Array.from({ length: 24 }, (_, h) => h);

// Minutos de 5 em 5 (00, 05, …, 55). 24h, sem AM/PM: abrange qualquer horário.
const MINUTOS = Array.from({ length: 12 }, (_, i) => i * 5);

const doisDigitos = (n) => String(n ?? 0).padStart(2, '0');

const contatoLinha = (pessoa) => (
  [pessoa.email, pessoa.phone].filter(Boolean).join(' · ') || 'sem contato cadastrado'
);

const BriefingsCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [briefings, setBriefings] = useState([]);
  const [eligible, setEligible] = useState([]);
  const [channels, setChannels] = useState({ telegram: {} });
  const [groupChatId, setGroupChatId] = useState('');
  const [discovered, setDiscovered] = useState([]);
  const [confirmRun, setConfirmRun] = useState(null);
  const [previewText, setPreviewText] = useState(null);
  const [addUserKey, setAddUserKey] = useState({});
  const [draftExtra, setDraftExtra] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/briefings`);
      setBriefings(data.briefings || []);
      setEligible(data.eligible_recipients || []);
      setChannels(data.channels || { telegram: {} });
      setGroupChatId(data.channels?.telegram?.group_chat_id || '');
    } catch (e) {
      toast.error('Não foi possível carregar os boletins');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchLocal = (key, patch) => {
    setBriefings((prev) => prev.map((b) => (b.key === key ? { ...b, ...patch } : b)));
  };

  const save = async (briefing) => {
    setSaving(briefing.key);
    try {
      await axios.put(`${API}/briefings/${briefing.key}`, {
        enabled: briefing.enabled,
        label: briefing.label,
        cadence: briefing.cadence,
        hour: briefing.hour,
        minute: briefing.minute ?? 0,
        weekday: briefing.weekday,
        day_of_month: briefing.day_of_month,
        channels: briefing.channels,
        recipient_ids: briefing.recipient_ids,
        extra_recipients: briefing.extra_recipients || [],
      });
      toast.success('Boletim salvo');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao salvar');
    } finally {
      setSaving(null);
    }
  };

  const preview = async (briefing) => {
    setSaving(`${briefing.key}-preview`);
    try {
      const { data } = await axios.post(`${API}/briefings/${briefing.key}/preview`);
      setPreviewText({ label: briefing.label, key: briefing.key, summary: data.summary });
      toast.success('Prévia montada. Ninguém foi notificado.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao montar a prévia');
    } finally {
      setSaving(null);
    }
  };

  const previewPdf = async (key) => {
    setSaving(`${key}-preview-pdf`);
    try {
      const { data } = await axios.post(`${API}/briefings/${key}/preview.pdf`, {}, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Falha ao gerar o PDF da prévia');
    } finally {
      setSaving(null);
    }
  };

  const test = async (briefing) => {
    setSaving(`${briefing.key}-test`);
    try {
      const { data } = await axios.post(`${API}/briefings/${briefing.key}/test`);
      const partes = [];
      if (data.email?.ok !== undefined) partes.push(data.email.ok ? 'e-mail OK' : 'e-mail FALHOU');
      if (data.whatsapp?.sent !== undefined) {
        partes.push(data.whatsapp.sent > 0 ? 'WhatsApp OK' : 'WhatsApp FALHOU');
      }
      if (data.telegram?.ok !== undefined) {
        partes.push(data.telegram.ok ? 'Telegram OK' : 'Telegram FALHOU');
      }
      const falhou = partes.some((p) => p.includes('FALHOU'));
      (falhou ? toast.error : toast.success)(
        partes.length ? `Enviado só para você — ${partes.join(' · ')}` : 'Nada a enviar',
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha no teste');
    } finally {
      setSaving(null);
    }
  };

  const run = async (briefing) => {
    setSaving(`${briefing.key}-run`);
    setConfirmRun(null);
    try {
      const { data } = await axios.post(`${API}/briefings/${briefing.key}/run`, {});
      const pessoas = data.recipients?.delivered_to || [];
      toast.success(`Boletim emitido para ${pessoas.length} destinatário(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha na emissão');
    } finally {
      setSaving(null);
    }
  };

  const saveTelegramGroup = async () => {
    setSaving('telegram');
    try {
      const { data } = await axios.put(`${API}/briefings/channels/telegram`, {
        group_chat_id: groupChatId,
      });
      setChannels(data);
      toast.success('Grupo do Telegram salvo');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao salvar o grupo');
    } finally {
      setSaving(null);
    }
  };

  const discoverChats = async () => {
    setSaving('discover');
    try {
      const { data } = await axios.post(`${API}/system-monitors/channels/telegram/discover`);
      setDiscovered(data.chats || []);
      if (!data.chats?.length) {
        toast.warning('Ninguém falou com o bot ainda. Mande uma mensagem no grupo e tente de novo.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao consultar o Telegram');
    } finally {
      setSaving(null);
    }
  };

  const addUser = (briefing, userId) => {
    if (!userId) return;
    const atuais = briefing.recipient_ids || [];
    if (atuais.includes(userId)) return;
    patchLocal(briefing.key, { recipient_ids: [...atuais, userId] });
    setAddUserKey((prev) => ({ ...prev, [briefing.key]: undefined }));
  };

  const removeUser = (briefing, userId) => {
    patchLocal(briefing.key, {
      recipient_ids: (briefing.recipient_ids || []).filter((id) => id !== userId),
    });
  };

  const addExtra = (briefing) => {
    const draft = draftExtra[briefing.key] || {};
    const nome = String(draft.full_name || '').trim();
    const email = String(draft.email || '').trim();
    const phone = String(draft.phone || '').trim();
    if (!nome || (!email && !phone)) {
      toast.error('Informe o nome e um e-mail ou telefone');
      return;
    }
    const novo = {
      id: `ext-${Date.now().toString(36)}`,
      full_name: nome,
      email,
      phone,
      kind: 'external',
    };
    patchLocal(briefing.key, {
      extra_recipients: [...(briefing.extra_recipients || []), novo],
    });
    setDraftExtra((prev) => ({ ...prev, [briefing.key]: { full_name: '', email: '', phone: '' } }));
  };

  const removeExtra = (briefing, extraId) => {
    patchLocal(briefing.key, {
      extra_recipients: (briefing.extra_recipients || []).filter((p) => p.id !== extraId),
    });
  };

  const setChannel = (briefing, canal, ligado) => {
    patchLocal(briefing.key, { channels: { ...briefing.channels, [canal]: ligado } });
  };

  const setCadence = (briefing, cadence) => {
    const patch = { cadence };
    if (cadence === 'weekly' && briefing.weekday == null) patch.weekday = 0;
    if (cadence === 'monthly' && briefing.day_of_month == null) patch.day_of_month = 1;
    patchLocal(briefing.key, patch);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-10 justify-center text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Carregando boletins…
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {briefings.map((b) => {
        const cadence = b.cadence;
        const ids = b.recipient_ids || [];
        const extras = b.extra_recipients || [];
        const porId = Object.fromEntries(eligible.map((p) => [p.id, p]));
        const incluidos = ids.map((id) => porId[id] || { id, full_name: 'Usuário removido', email: null, phone: null });
        const disponiveis = eligible.filter((p) => !ids.includes(p.id));
        const draft = draftExtra[b.key] || {};
        return (
          <Card key={b.key} data-testid={`briefing-card-${b.key}`}>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 flex-1 min-w-0">
                  <FileText className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0 space-y-2">
                    <Label htmlFor={`briefing-name-${b.key}`}>Nome do boletim</Label>
                    <Input
                      id={`briefing-name-${b.key}`}
                      value={b.label || ''}
                      onChange={(e) => patchLocal(b.key, { label: e.target.value })}
                      data-testid={`briefing-label-${b.key}`}
                    />
                    <CardDescription>{b.description}</CardDescription>
                  </div>
                </div>
                <Switch
                  checked={b.enabled}
                  onCheckedChange={(v) => patchLocal(b.key, { enabled: v })}
                  data-testid={`briefing-enabled-${b.key}`}
                />
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label>Frequência</Label>
                  <Select
                    value={cadence}
                    onValueChange={(v) => setCadence(b, v)}
                  >
                    <SelectTrigger data-testid={`briefing-cadence-${b.key}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FREQUENCIAS.map((f) => (
                        <SelectItem key={f.valor} value={f.valor}>{f.rotulo}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {(FREQUENCIAS.find((f) => f.valor === cadence) || {}).detalhe}
                  </p>
                </div>

                {cadence === 'weekly' && (
                  <div className="space-y-2">
                    <Label>Repete no dia</Label>
                    <Select
                      value={String(b.weekday ?? 0)}
                      onValueChange={(v) => patchLocal(b.key, { weekday: Number(v) })}
                    >
                      <SelectTrigger data-testid={`briefing-weekday-${b.key}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DIAS_DA_SEMANA.map((d) => (
                          <SelectItem key={d.valor} value={String(d.valor)}>{d.rotulo}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {cadence === 'monthly' && (
                  <div className="space-y-2">
                    <Label>Repete no dia</Label>
                    <Input
                      type="number"
                      min={1}
                      max={28}
                      value={b.day_of_month ?? 1}
                      onChange={(e) => patchLocal(b.key, { day_of_month: Number(e.target.value) })}
                      data-testid={`briefing-day-${b.key}`}
                    />
                    <p className="text-xs text-muted-foreground">
                      Até 28 — dia 29 a 31 não existe em todo mês.
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Horário de envio</Label>
                  <div className="flex items-center gap-2" data-testid={`briefing-time-${b.key}`}>
                    <Select
                      value={String(b.hour ?? 6)}
                      onValueChange={(v) => patchLocal(b.key, { hour: Number(v) })}
                    >
                      <SelectTrigger data-testid={`briefing-hour-${b.key}`} className="flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {HORAS.map((h) => (
                          <SelectItem key={h} value={String(h)}>
                            {doisDigitos(h)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-muted-foreground">:</span>
                    <Select
                      value={String(b.minute ?? 0)}
                      onValueChange={(v) => patchLocal(b.key, { minute: Number(v) })}
                    >
                      <SelectTrigger data-testid={`briefing-minute-${b.key}`} className="flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {MINUTOS.map((m) => (
                          <SelectItem key={m} value={String(m)}>
                            {doisDigitos(m)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Formato 24h. Hora e minuto (de 5 em 5).
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Última emissão</Label>
                  <p className="text-sm text-muted-foreground pt-2">
                    {b.last_run_date || 'ainda não emitido'}
                  </p>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Send className="w-4 h-4" /> Canais</Label>
                <div className="flex flex-wrap gap-6">
                  {[
                    ['email', 'E-mail (PDF completo)'],
                    ['whatsapp', 'WhatsApp (resumo + PDF)'],
                    ['telegram', 'Telegram (resumo no grupo)'],
                  ].map(([canal, rotulo]) => (
                    <label key={canal} className="flex items-center gap-2 text-sm">
                      <Switch
                        checked={b.channels?.[canal] !== false}
                        onCheckedChange={(v) => setChannel(b, canal, v)}
                        data-testid={`briefing-channel-${b.key}-${canal}`}
                      />
                      {rotulo}
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Users className="w-4 h-4" /> Destinatários
                </Label>
                {incluidos.length === 0 && extras.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Ninguém na lista. Inclua um usuário cadastrado ou um contato
                    só para este boletim.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {incluidos.map((pessoa) => (
                      <li
                        key={pessoa.id}
                        className="flex items-center gap-2 text-sm rounded border px-3 py-2"
                      >
                        <span className="flex-1 min-w-0">
                          <span className="block truncate">{pessoa.full_name}</span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {contatoLinha(pessoa)}
                          </span>
                        </span>
                        <Badge variant="secondary">usuário</Badge>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          onClick={() => removeUser(b, pessoa.id)}
                          data-testid={`briefing-remove-${b.key}-${pessoa.id}`}
                          aria-label={`Excluir ${pessoa.full_name}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </li>
                    ))}
                    {extras.map((pessoa) => (
                      <li
                        key={pessoa.id}
                        className="flex items-center gap-2 text-sm rounded border px-3 py-2"
                      >
                        <span className="flex-1 min-w-0">
                          <span className="block truncate">{pessoa.full_name}</span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {contatoLinha(pessoa)}
                          </span>
                        </span>
                        <Badge variant="outline">contato avulso</Badge>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          onClick={() => removeExtra(b, pessoa.id)}
                          data-testid={`briefing-remove-extra-${b.key}-${pessoa.id}`}
                          aria-label={`Excluir ${pessoa.full_name}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="flex flex-wrap gap-2 items-end">
                  <div className="space-y-1 min-w-[220px] flex-1">
                    <Label className="text-xs text-muted-foreground">Incluir usuário cadastrado</Label>
                    <Select
                      value={addUserKey[b.key] || undefined}
                      onValueChange={(v) => addUser(b, v)}
                      disabled={disponiveis.length === 0}
                    >
                      <SelectTrigger data-testid={`briefing-add-user-${b.key}`}>
                        <SelectValue placeholder={disponiveis.length ? 'Escolher usuário…' : 'Todos já estão na lista'} />
                      </SelectTrigger>
                      <SelectContent>
                        {disponiveis.map((pessoa) => (
                          <SelectItem key={pessoa.id} value={pessoa.id}>
                            {pessoa.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="rounded border p-3 space-y-2">
                  <Label className="flex items-center gap-2 text-sm">
                    <UserPlus className="w-4 h-4" /> Incluir contato sem cadastro
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Só recebe este boletim. Não cria usuário no sistema.
                  </p>
                  <div className="grid gap-2 sm:grid-cols-3">
                    <Input
                      placeholder="Nome"
                      value={draft.full_name || ''}
                      onChange={(e) => setDraftExtra((prev) => ({
                        ...prev, [b.key]: { ...draft, full_name: e.target.value },
                      }))}
                      data-testid={`briefing-extra-name-${b.key}`}
                    />
                    <Input
                      type="email"
                      placeholder="E-mail"
                      value={draft.email || ''}
                      onChange={(e) => setDraftExtra((prev) => ({
                        ...prev, [b.key]: { ...draft, email: e.target.value },
                      }))}
                      data-testid={`briefing-extra-email-${b.key}`}
                    />
                    <Input
                      placeholder="WhatsApp"
                      value={draft.phone || ''}
                      onChange={(e) => setDraftExtra((prev) => ({
                        ...prev, [b.key]: { ...draft, phone: e.target.value },
                      }))}
                      data-testid={`briefing-extra-phone-${b.key}`}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="gap-2"
                    onClick={() => addExtra(b)}
                    data-testid={`briefing-add-extra-${b.key}`}
                  >
                    <Plus className="w-4 h-4" /> Adicionar contato
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button onClick={() => save(b)} disabled={saving === b.key}>
                  {saving === b.key ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
                </Button>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => preview(b)}
                  disabled={saving === `${b.key}-preview`}
                >
                  <Eye className="w-4 h-4" /> Prévia (não envia)
                </Button>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => previewPdf(b.key)}
                  disabled={saving === `${b.key}-preview-pdf`}
                >
                  <FileText className="w-4 h-4" /> Ver o PDF
                </Button>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => test(b)}
                  disabled={saving === `${b.key}-test`}
                >
                  <Send className="w-4 h-4" /> Testar (só para mim)
                </Button>
                <Button
                  variant="secondary"
                  className="gap-2"
                  onClick={() => setConfirmRun(b)}
                  disabled={saving === `${b.key}-run`}
                >
                  <Radio className="w-4 h-4" /> Emitir agora
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Grupo do Telegram</CardTitle>
          <CardDescription>
            Destino do boletim no Telegram. O Telegram não entrega por número de telefone:
            o endereço é um chat_id, e o bot só escreve para quem já falou com ele. Grupos
            têm id negativo — um id positivo colado do app é de usuário e dá “chat not found”.
            O token do bot continua em Monitoramento, abaixo.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="space-y-2 flex-1 min-w-[220px]">
              <Label>chat_id do grupo</Label>
              <Input
                value={groupChatId}
                onChange={(e) => setGroupChatId(e.target.value)}
                placeholder="-5335584825"
                data-testid="briefing-telegram-group"
              />
            </div>
            <Button onClick={saveTelegramGroup} disabled={saving === 'telegram'}>
              Salvar grupo
            </Button>
            <Button variant="outline" onClick={discoverChats} disabled={saving === 'discover'}>
              Procurar conversas
            </Button>
          </div>

          {discovered.length > 0 && (
            <div className="space-y-1">
              {discovered.map((chat) => (
                <button
                  key={chat.chat_id}
                  type="button"
                  className="flex w-full items-center justify-between rounded border px-3 py-2 text-sm hover:bg-muted"
                  onClick={() => setGroupChatId(chat.chat_id)}
                >
                  <span>{chat.title || chat.first_name || chat.chat_id}</span>
                  <span className="text-xs text-muted-foreground">
                    {chat.type} · {chat.chat_id}
                  </span>
                </button>
              ))}
            </div>
          )}

          {!channels.telegram?.configured && (
            <p className="text-sm text-amber-600">
              Bot do Telegram ainda não configurado — cadastre o token em Monitoramento.
            </p>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={Boolean(previewText)} onOpenChange={(v) => !v && setPreviewText(null)}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Prévia — {previewText?.label}</AlertDialogTitle>
            <AlertDialogDescription>
              É o texto que sai no WhatsApp e no Telegram. Ninguém foi notificado.
              O detalhamento completo está no PDF.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <pre className="max-h-[50vh] overflow-auto whitespace-pre-wrap rounded bg-muted p-3 text-xs">
            {previewText?.summary}
          </pre>
          <AlertDialogFooter>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => previewPdf(previewText.key)}
            >
              <FileText className="w-4 h-4" /> Ver o PDF
            </Button>
            <AlertDialogCancel>Fechar</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(confirmRun)} onOpenChange={(v) => !v && setConfirmRun(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Emitir o boletim agora?</AlertDialogTitle>
            <AlertDialogDescription>
              A emissão é real: {confirmRun?.label} vai para todos os destinatários da lista,
              pelos canais ligados. São pessoas recebendo — para conferir o conteúdo sem
              notificar ninguém, use “Prévia”; para provar os canais, use “Testar (só para mim)”.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => run(confirmRun)} data-testid="confirm-run-briefing">
              Emitir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BriefingsCard;
